import type { Agent } from "@cc/protocol";
import { DEFAULT_MATCH_SECONDS } from "@cc/engine";
import { createLlmAgent } from "./llm-agent.js";
import { createAnthropicProvider, createOpenAiCompatibleProvider } from "./provider.js";
import type { ModelProvider } from "./provider.js";

/**
 * Browser-safe model wiring.
 *
 * This is a *separate entry point* from `@cc/agents` on purpose. `presets.ts`
 * reads `process.env`, so it can never be pulled into a browser bundle, and a
 * bundler that merely fails to tree-shake one import would break the whole site
 * with a `process is not defined` at runtime. Keeping the browser surface in its
 * own module means the dependency edge simply does not exist.
 *
 * Security posture, stated plainly because it is a real trade-off and not an
 * oversight:
 *
 *   - The user's API key lives **in memory only**, for the lifetime of the page.
 *     It is never written to localStorage, a cookie, the URL, the action log or
 *     the match result. Reloading the page forgets it.
 *   - Requests go **directly from the browser to the provider**. There is no
 *     backend proxy, so nothing is logged on a server we control — and equally,
 *     there is no server to protect the key from same-origin script injection.
 *     `003.md` §11 describes the more secure proxy architecture; this build
 *     deliberately takes the zero-setup path the user chose, and the UI says so
 *     rather than implying a guarantee we do not make.
 *   - Crickets are saved *without* keys, so a saved cricket is a configuration,
 *     not a credential.
 */

/** A provider the user can pick from, with everything except the key preset. */
export interface ProviderPreset {
  readonly id: string;
  readonly label: string;
  /** Wire format: Anthropic's API differs from the OpenAI-compatible one. */
  readonly kind: "openai" | "anthropic";
  readonly baseUrl: string;
  readonly models: readonly string[];
  /** Where to get a key, shown in the connection-test failure path. */
  readonly keyUrl: string;
  /**
   * Default ball icon for this provider (image URL under `/icons/…`).
   *
   * Shown on the arena cricket instead of the generic A/B letter. Users can
   * still override with an emoji in the duel modal.
   */
  readonly icon: string;
  /** True for the catch-all entry, which asks the user for everything. */
  readonly custom?: boolean;
}

/**
 * Shared sampling / reliability knobs for every arena cricket, regardless of
 * which vendor the user picked.
 *
 * One policy, many providers: temperature low enough that the six-verb schema
 * holds; JSON mode on; thinking off (except explicit reasoner models); retries
 * for the empty-content / 429 blips DeepSeek-class APIs are known for.
 */
export function arenaLlmOptions(model: string): {
  readonly temperature: number;
  readonly maxTokens: number;
  readonly jsonMode: boolean;
  readonly disableThinking: boolean;
  readonly maxRetries: number;
  readonly timeoutMs: number;
} {
  const thinkingModel = /reasoner|r1|thinking/i.test(model);
  return {
    temperature: 0.3,
    maxTokens: thinkingModel ? 1024 : 384,
    jsonMode: true,
    disableThinking: !thinkingModel,
    maxRetries: 2,
    // Live arena turns wait on the model; give reasoning models real headroom
    // rather than aborting mid-thought into a heuristic fallback.
    timeoutMs: thinkingModel ? 55_000 : 35_000,
  };
}

/**
 * Mainstream providers, preconfigured so a user only pastes a key.
 *
 * Every one of these speaks the OpenAI chat-completions shape except Anthropic,
 * which is why `kind` exists. Base URLs are the documented public endpoints;
 * the Custom entry exists so an unlisted or self-hosted gateway still works.
 */
export const PROVIDER_PRESETS: readonly ProviderPreset[] = [
  {
    id: "openai",
    label: "OpenAI",
    kind: "openai",
    baseUrl: "https://api.openai.com/v1",
    models: ["gpt-4o-mini", "gpt-4o", "gpt-4.1-mini"],
    keyUrl: "https://platform.openai.com/api-keys",
    icon: "/icons/openai.svg",
  },
  {
    id: "anthropic",
    label: "Anthropic",
    kind: "anthropic",
    baseUrl: "https://api.anthropic.com",
    models: ["claude-3-5-haiku-latest", "claude-3-5-sonnet-latest"],
    keyUrl: "https://console.anthropic.com/settings/keys",
    icon: "/icons/anthropic.svg",
  },
  {
    id: "deepseek",
    label: "DeepSeek",
    kind: "openai",
    baseUrl: "https://api.deepseek.com/v1",
    models: ["deepseek-chat", "deepseek-reasoner"],
    keyUrl: "https://platform.deepseek.com/api_keys",
    icon: "/icons/deepseek.svg",
  },
  {
    id: "doubao",
    label: "Doubao (豆包 / 火山方舟)",
    kind: "openai",
    baseUrl: "https://ark.cn-beijing.volces.com/api/v3",
    models: ["doubao-seed-1-6-250615", "doubao-1.5-pro-32k-250115", "doubao-1.5-lite-32k-250115"],
    keyUrl: "https://console.volcengine.com/ark",
    icon: "/icons/doubao.png",
  },
  {
    id: "moonshot",
    label: "Kimi (Moonshot)",
    kind: "openai",
    baseUrl: "https://api.moonshot.cn/v1",
    models: ["kimi-k2.5", "kimi-k3", "moonshot-v1-8k", "kimi-k2-0711-preview"],
    keyUrl: "https://platform.moonshot.cn/console/api-keys",
    icon: "/icons/kimi.svg",
  },
  {
    id: "mimo",
    label: "Xiaomi MiMo",
    kind: "openai",
    baseUrl: "https://api.xiaomimimo.com/v1",
    models: ["mimo-v2.5-pro", "mimo-v2.6-pro", "mimo-v2.6-flash"],
    keyUrl: "https://platform.xiaomimimo.com/",
    icon: "/icons/mimo.svg",
  },
  {
    id: "qwen",
    label: "Qwen (DashScope)",
    kind: "openai",
    baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1",
    models: ["qwen-plus", "qwen-max", "qwen-turbo"],
    keyUrl: "https://bailian.console.aliyun.com/",
    icon: "/icons/qwen.svg",
  },
  {
    id: "minimax",
    label: "MiniMax",
    kind: "openai",
    baseUrl: "https://api.minimaxi.com/v1",
    models: ["MiniMax-M3", "MiniMax-M2.5", "MiniMax-M2"],
    keyUrl: "https://platform.minimaxi.com/",
    icon: "/icons/minimax.svg",
  },
  {
    id: "stepfun",
    label: "StepFun (阶跃星辰)",
    kind: "openai",
    baseUrl: "https://api.stepfun.com/v1",
    models: ["step-3.7-flash", "step-3.5-flash", "step-2-mini"],
    keyUrl: "https://platform.stepfun.com/",
    icon: "/icons/stepfun.svg",
  },
  {
    id: "zhipu",
    label: "Zhipu (GLM)",
    kind: "openai",
    baseUrl: "https://open.bigmodel.cn/api/paas/v4",
    models: ["glm-4-flash", "glm-4-plus"],
    keyUrl: "https://open.bigmodel.cn/usercenter/apikeys",
    icon: "/icons/zhipu.svg",
  },
  {
    id: "siliconflow",
    label: "SiliconFlow (硅基流动)",
    kind: "openai",
    baseUrl: "https://api.siliconflow.cn/v1",
    models: ["deepseek-ai/DeepSeek-V3", "Qwen/Qwen2.5-7B-Instruct", "THUDM/glm-4-9b-chat"],
    keyUrl: "https://cloud.siliconflow.cn/account/ak",
    icon: "/icons/siliconflow.svg",
  },
  {
    id: "google",
    label: "Google (Gemini, OpenAI-compatible)",
    kind: "openai",
    baseUrl: "https://generativelanguage.googleapis.com/v1beta/openai",
    models: ["gemini-2.0-flash", "gemini-1.5-flash"],
    keyUrl: "https://aistudio.google.com/app/apikey",
    icon: "/icons/google.svg",
  },
  {
    id: "xai",
    label: "xAI (Grok)",
    kind: "openai",
    baseUrl: "https://api.x.ai/v1",
    models: ["grok-2-latest", "grok-3-mini"],
    keyUrl: "https://console.x.ai/",
    icon: "/icons/xai.svg",
  },
  {
    id: "mistral",
    label: "Mistral",
    kind: "openai",
    baseUrl: "https://api.mistral.ai/v1",
    models: ["mistral-small-latest", "mistral-large-latest"],
    keyUrl: "https://console.mistral.ai/api-keys/",
    icon: "/icons/mistral.svg",
  },
  {
    id: "openrouter",
    label: "OpenRouter",
    kind: "openai",
    baseUrl: "https://openrouter.ai/api/v1",
    models: ["openai/gpt-4o-mini", "anthropic/claude-3.5-haiku", "deepseek/deepseek-chat"],
    keyUrl: "https://openrouter.ai/keys",
    icon: "/icons/openrouter.svg",
  },
  {
    id: "custom",
    label: "Custom / self-hosted",
    kind: "openai",
    baseUrl: "",
    models: [],
    keyUrl: "",
    icon: "/icons/custom.svg",
    custom: true,
  },
];

export function findProviderPreset(id: string): ProviderPreset {
  return PROVIDER_PRESETS.find((p) => p.id === id) ?? PROVIDER_PRESETS[0]!;
}

/**
 * @deprecated Kept so older callers compile; no preset uses a dev proxy anymore.
 */
export function presetBaseUrl(preset: ProviderPreset, _options: { readonly devServer?: boolean } = {}): string {
  return preset.baseUrl;
}

/** @deprecated No preset requires a dev proxy after the internal gateway was removed. */
export function isDevProxyOnly(_preset: ProviderPreset): boolean {
  return false;
}

export interface CricketBrainConfig {
  readonly id: string;
  readonly persona: string;
  readonly providerId: string;
  readonly baseUrl: string;
  readonly model: string;
  /** Never persisted. See the security note above. */
  readonly apiKey: string;
  readonly arenaSize?: number;
  readonly matchSeconds?: number;
  /** Ignored; retained for call-site compatibility. */
  readonly devServer?: boolean;
}

/**
 * Builds the provider used for live match decisions.
 *
 * Every vendor path — OpenAI, DeepSeek, Qwen, Moonshot, Zhipu, Gemini,
 * OpenRouter, Custom, and Anthropic — gets the same arena constraints so a
 * cricket's reliability does not depend on which dropdown the user picked.
 */
export function createProviderFor(config: CricketBrainConfig): ModelProvider {
  const preset = findProviderPreset(config.providerId);
  const baseUrl = config.baseUrl.trim() || preset.baseUrl;
  if (!baseUrl) {
    throw new Error("A base URL is required for a custom provider.");
  }
  if (config.apiKey.trim() === "") {
    throw new Error("An API key is required.");
  }

  const arena = arenaLlmOptions(config.model);

  if (preset.kind === "anthropic") {
    return createAnthropicProvider({
      name: preset.id,
      apiKey: config.apiKey,
      model: config.model,
      baseUrl,
      temperature: arena.temperature,
      maxTokens: arena.maxTokens,
      maxRetries: arena.maxRetries,
      timeoutMs: arena.timeoutMs,
    });
  }

  return createOpenAiCompatibleProvider({
    name: preset.id,
    apiKey: config.apiKey,
    baseUrl,
    model: config.model,
    temperature: arena.temperature,
    maxTokens: arena.maxTokens,
    jsonMode: arena.jsonMode,
    disableThinking: arena.disableThinking,
    maxRetries: arena.maxRetries,
    timeoutMs: arena.timeoutMs,
  });
}

/** Builds a runnable cricket backed by a real model. */
export function createCricketAgent(config: CricketBrainConfig): Agent {
  return createLlmAgent({
    id: config.id,
    persona: config.persona,
    model: config.model,
    provider: createProviderFor(config),
    arenaSize: config.arenaSize ?? 100,
    matchSeconds: config.matchSeconds ?? DEFAULT_MATCH_SECONDS,
  });
}

export type ConnectionTestResult =
  | { readonly ok: true; readonly latencyMs: number; readonly reply: string }
  | { readonly ok: false; readonly stage: "auth" | "model" | "network"; readonly message: string };

/**
 * Checks a key/model pair before the user commits to a match.
 *
 * Uses the same JSON-constrained provider as a live match, and asks for a tiny
 * JSON object, so a "green" test means the arena request shape actually works —
 * not just that a free-form chat ping got through.
 */
export async function testConnection(
  config: CricketBrainConfig,
  timeoutMs = 15_000,
): Promise<ConnectionTestResult> {
  let provider: ModelProvider;
  try {
    provider = createProviderFor(config);
  } catch (err) {
    return {
      ok: false,
      stage: "auth",
      message: err instanceof Error ? err.message : String(err),
    };
  }

  const started = Date.now();
  try {
    const reply = await withTimeout(
      provider.complete([
        {
          role: "user",
          content: 'Reply with ONLY this JSON object: {"ok":true}',
        },
      ]),
      timeoutMs,
    );
    return { ok: true, latencyMs: Date.now() - started, reply: reply.trim().slice(0, 80) };
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    return { ok: false, stage: classifyFailure(message), message };
  }
}

/**
 * Distinguishes a bad key from a bad model name or a dead network.
 *
 * Worth the small amount of string matching: the three failures need three
 * different actions from the user, and "Authentication failed" when the real
 * problem is a typo in the model name sends them down the wrong path.
 */
function classifyFailure(message: string): "auth" | "model" | "network" {
  const m = message.toLowerCase();
  if (m.includes("401") || m.includes("403") || m.includes("unauthor") || m.includes("api key")) {
    return "auth";
  }
  if (m.includes("404") || m.includes("model") || m.includes("not found")) return "model";
  return "network";
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(`Timed out after ${ms}ms`)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        clearTimeout(timer);
        reject(err);
      },
    );
  });
}
